import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import { DateTimePicker, DropdownList } from 'react-widgets';
import Multiselect from 'multiselect-react-dropdown';
import moment from 'moment';
import NonBilledSessionsReportGrid from './components/NonBilledSessionsReportGrid';
import { Paginated } from 'src/services/api-handlers/pagination';
import RoleLayout from 'src/components/layout/RoleLayout';
import Loader from 'src/components/Loader';
import { INonBilledSession } from 'src/store/reducers/nonBilledSessionsReport-reducer';
import CaseEnum from 'src/enumerables/case-enum';
import { getValFromEnum } from 'src/components/redux-form/utils/dropdown-values';
import gridStyles from 'src/components/react-table/assets/grid.module.scss';
import styles from './assets/screen.module.scss';

export interface StateProps {
    data: Paginated<INonBilledSession>;
    inProgress: boolean;
    students: SelectObject[];
    serviceTypes: SelectObject[];
}

export interface DispatchProps {
    getData: (params: any) => void;
    getDataCsv: (params: any) => void;
    getStudents: (params: any) => void;
    getServiceTypes: (params: any) => void;
}

export interface OwnProps {}

export interface Props extends StateProps, DispatchProps, OwnProps {}

type FilterMode = 'exclude' | 'include';

interface ListFilter {
    mode: FilterMode;
    selected: SelectObject[];
}

const modeOptions: { value: FilterMode; label: string }[] = [
    { value: 'exclude', label: 'Exclude selected' },
    { value: 'include', label: 'Include only selected' },
];

const emptyFilter: ListFilter = { mode: 'exclude', selected: [] };

const multiselectStyle = {
    chips: { background: '#3e81b9', borderRadius: '5px', cursor: 'pointer' },
    searchBox: { cursor: 'pointer', background: '#fff' },
    multiselectContainer: { color: '#3e81b9', zIndex: '15' },
    optionContainer: { zIndex: '15' },
    option: { background: 'white', color: '#3e81b9' },
};

interface ListFilterBlockProps {
    title: string;
    options: SelectObject[];
    value: ListFilter;
    onChange: (value: ListFilter) => void;
}

const ListFilterBlock: React.FC<ListFilterBlockProps> = ({ title, options, value, onChange }) => (
    <div className={classNames(gridStyles.itemFilterBlock, styles.listFilter, 'mb-3 mr-3')}>
        <span>{title}: </span>
        <DropdownList
            containerClassName={styles.modeSelect}
            data={modeOptions}
            textField="label"
            valueField="value"
            value={value.mode}
            onChange={(item: { value: FilterMode }) => onChange({ ...value, mode: item.value })}
        />
        <Multiselect
            options={options}
            selectedValues={value.selected}
            onSelect={(list: SelectObject[]) => onChange({ ...value, selected: list })}
            onRemove={(list: SelectObject[]) => onChange({ ...value, selected: list })}
            displayValue="label"
            closeIcon="close"
            placeholder="Search..."
            style={multiselectStyle}
            emptyRecordMsg=""
            showArrow={true}
        />
    </div>
);

const NonBilledSessionsReportList: React.FC<Props> = ({
    data,
    inProgress,
    students,
    serviceTypes,
    getData,
    getDataCsv,
    getStudents,
    getServiceTypes,
}) => {
    const caseTypes: SelectObject[] = getValFromEnum(CaseEnum ? CaseEnum.getEnum('caseType') : {});

    // Invoices are generated for the previous month, so it is the default period
    const [dateFrom, setDateFrom] = useState<Date | undefined>(moment().subtract(1, 'month').startOf('month').toDate());
    const [dateTo, setDateTo] = useState<Date | undefined>(moment().subtract(1, 'month').endOf('month').toDate());
    const [serviceTypeFilter, setServiceTypeFilter] = useState<ListFilter>(emptyFilter);
    const [studentFilter, setStudentFilter] = useState<ListFilter>(emptyFilter);
    const [caseTypeFilter, setCaseTypeFilter] = useState<ListFilter>(emptyFilter);
    // Filters of the last "Generate Report"; the grid pages through them
    const [submittedParams, setSubmittedParams] = useState<Record<string, unknown> | null>(null);
    const [gridKey, setGridKey] = useState(0);

    useEffect(() => {
        getStudents({ page: 1 });
        getServiceTypes({ page: 1, itemsPerPage: 1200 });
    }, []);

    const isValid = dateFrom !== undefined && dateTo !== undefined && !moment(dateFrom).isAfter(dateTo, 'day');

    const buildParams = useCallback(() => {
        const params: Record<string, unknown> = {
            dateFrom: moment(dateFrom).format('YYYY-MM-DD'),
            dateTo: moment(dateTo).format('YYYY-MM-DD'),
        };
        const addList = (name: string, filter: ListFilter) => {
            if (!filter.selected.length) return;
            params[`${name}Ids`] = filter.selected.map((it) => it.value);
            params[`${name}Mode`] = filter.mode;
        };
        addList('serviceType', serviceTypeFilter);
        addList('student', studentFilter);
        addList('caseType', caseTypeFilter);
        return params;
    }, [dateFrom, dateTo, serviceTypeFilter, studentFilter, caseTypeFilter]);

    const handleSubmit = () => {
        if (!isValid) return;
        setSubmittedParams(buildParams());
        setGridKey((key) => key + 1);
    };

    const handleCsvExport = () => {
        if (!isValid) return;
        // export what the table shows; before the first report use the current filters
        getDataCsv(submittedParams ?? buildParams());
    };

    const handleGridData = useCallback(
        (params: { page?: number; itemsPerPage?: number }) => {
            if (!submittedParams) return;
            getData({ ...submittedParams, page: params.page ?? 1, itemsPerPage: params.itemsPerPage });
        },
        [submittedParams, getData],
    );

    const hasSearched = submittedParams !== null;
    const total = data.totalCount && data.totalCount > 0 ? data.totalCount : 0;

    return (
        <RoleLayout>
            <div className={classNames('pageHeading')}>
                <h2 className={classNames('pageHeadingTitle')}>
                    NON-BILLED SESSIONS REPORT {hasSearched && !inProgress ? `(${total})` : ''}
                </h2>
                {isValid && (
                    <button type="button" className="btn btn-outline-info" onClick={handleCsvExport}>
                        <FontAwesomeIcon className="mr-1" icon={faDownload} />
                        Export .csv
                    </button>
                )}
            </div>

            <div className={classNames(gridStyles.firstFilterBlock, styles.filterWrap)}>
                <ListFilterBlock
                    title="Service types"
                    options={serviceTypes}
                    value={serviceTypeFilter}
                    onChange={setServiceTypeFilter}
                />
                <ListFilterBlock
                    title="Students"
                    options={students}
                    value={studentFilter}
                    onChange={setStudentFilter}
                />
                <ListFilterBlock
                    title="Case types"
                    options={caseTypes}
                    value={caseTypeFilter}
                    onChange={setCaseTypeFilter}
                />

                <div className={classNames('w-100', styles.secondRow)}>
                    <div className={classNames(gridStyles.itemFilterBlock, 'mb-3 mr-3')}>
                        <span>From: </span>
                        <DateTimePicker
                            value={dateFrom}
                            onChange={(date: Date | undefined) => setDateFrom(date ?? undefined)}
                            time={false}
                            date={true}
                            format={'MMM D, YYYY'}
                        />
                    </div>

                    <div className={classNames(gridStyles.itemFilterBlock, 'mb-3 mr-3')}>
                        <span>To: </span>
                        <DateTimePicker
                            value={dateTo}
                            onChange={(date: Date | undefined) => setDateTo(date ?? undefined)}
                            time={false}
                            date={true}
                            format={'MMM D, YYYY'}
                        />
                    </div>

                    <div className={classNames(gridStyles.itemFilterBlock, 'mb-3')}>
                        <button type="button" className="btn btn-info" disabled={!isValid} onClick={handleSubmit}>
                            Generate Report
                        </button>
                    </div>
                </div>
            </div>

            <div className={classNames(styles.list)}>
                {inProgress ? <Loader /> : null}
                {hasSearched ? (
                    // page: 1 so a new report starts from the first page (Grid reads it only on mount)
                    <NonBilledSessionsReportGrid key={gridKey} data={{ ...data, page: 1 }} getData={handleGridData} />
                ) : null}
            </div>
        </RoleLayout>
    );
};

export default NonBilledSessionsReportList;
